import { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { useNote, useTypes } from '../../hooks/useVault';
import { useUi } from '../../store/ui';
import { readFrontmatter, setFrontmatterKey, coerceValue } from '../../lib/frontmatter';

type PropSchema = Record<string, unknown> & { type?: string | string[]; enum?: unknown[]; items?: Record<string, unknown>; description?: string };

/** Frontmatter as a form, driven by the note type's JSON Schema. Edits go into the draft, so autosave handles them. */
export function PropertiesPanel({ noteId }: { noteId: string | null }) {
  const note = useNote(noteId);
  const types = useTypes();
  const draft = useUi((s) => (noteId ? s.drafts[noteId] : undefined));
  const setDraft = useUi((s) => s.setDraft);
  const [newKey, setNewKey] = useState('');

  const raw = draft?.raw ?? note.data?.raw ?? '';
  const fm = useMemo(() => readFrontmatter(raw), [raw]);
  if (!noteId || !note.data) return <div className="dock-empty">Open a note to edit its properties.</div>;

  const type = String(fm.type ?? note.data.type);
  const def = types.data?.find((t) => t.key === type);
  const props = ((def?.fieldSchema as { properties?: Record<string, PropSchema> })?.properties ?? {}) as Record<string, PropSchema>;
  const required = new Set(((def?.fieldSchema as { required?: string[] })?.required ?? []) as string[]);
  const keys = [...new Set(['type', ...Object.keys(props).filter((k) => k !== 'title'), ...Object.keys(fm).filter((k) => k !== 'title')])];

  const set = (k: string, v: unknown) => setDraft(noteId, { raw: setFrontmatterKey(raw, k, v), dirty: true, baseHash: draft?.baseHash ?? note.data!.contentHash }, 'external');

  return (
    <div>
      {note.data.schemaWarnings.length > 0 && (
        <div className="props" style={{ marginBottom: 8 }}>
          {note.data.schemaWarnings.map((w, i) => (
            <div key={i} className="warn-row">⚠ {w.path || '/'} {w.message}</div>
          ))}
        </div>
      )}
      <div className="props">
        {keys.map((k) => {
          const schema = k === 'type' ? { type: 'string', enum: (types.data ?? []).map((t) => t.key) } : props[k];
          const v = fm[k];
          const isArray = Array.isArray(v) || schema?.type === 'array';
          const typesOf = Array.isArray(schema?.type) ? schema.type : schema?.type ? [schema.type] : [];
          return (
            <FieldRow key={k} k={k} required={required.has(k)} description={schema?.description}>
              {schema?.enum ? (
                <select value={String(v ?? '')} onChange={(e) => set(k, e.target.value || undefined)}>
                  <option value="">—</option>
                  {schema.enum.map((o) => (
                    <option key={String(o)} value={String(o)}>{String(o)}</option>
                  ))}
                </select>
              ) : isArray ? (
                <ArrayField values={Array.isArray(v) ? v.map(String) : v == null ? [] : [String(v)]} onChange={(vals) => set(k, vals)} />
              ) : typesOf.includes('boolean') ? (
                <select value={v === true ? 'true' : v === false ? 'false' : ''} onChange={(e) => set(k, coerceValue(schema, e.target.value))}>
                  <option value="">—</option>
                  <option value="true">true</option>
                  <option value="false">false</option>
                </select>
              ) : (
                <input
                  type="text"
                  className={typesOf.includes('number') || typesOf.includes('integer') || /ip|serial|phone|code/.test(k) ? 'mono' : ''}
                  defaultValue={v == null ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v)}
                  key={`${k}:${String(v)}`}
                  onBlur={(e) => {
                    const next = coerceValue(schema, e.target.value);
                    if (next !== v && !(next === undefined && v == null)) set(k, next);
                  }}
                  onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
                  placeholder={schema?.description}
                />
              )}
            </FieldRow>
          );
        })}
        <span className="k">+ field</span>
        <input
          type="text"
          value={newKey}
          onChange={(e) => setNewKey(e.target.value)}
          placeholder="key, Enter"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && /^[A-Za-z_][\w-]*$/.test(newKey) && !(newKey in fm)) {
              set(newKey, '');
              setNewKey('');
            }
          }}
        />
      </div>
      {def && (
        <div className="label" style={{ marginTop: 14 }}>
          schema: {def.label} <span style={{ color: 'var(--text-faint)' }}>({Object.keys(props).length} fields, advisory)</span>
        </div>
      )}
    </div>
  );
}

function FieldRow({ k, required, description, children }: { k: string; required: boolean; description?: string; children: React.ReactNode }) {
  return (
    <>
      <span className={`k${required ? ' req' : ''}`} title={description ?? k}>{k}</span>
      <span>{children}</span>
    </>
  );
}

function ArrayField({ values, onChange }: { values: string[]; onChange: (v: string[]) => void }) {
  const [text, setText] = useState('');
  return (
    <span className="arr">
      {values.map((v, i) => (
        <span key={`${v}-${i}`} className="chip" onClick={() => onChange(values.filter((_, j) => j !== i))} title="remove">
          {v} <X size={10} />
        </span>
      ))}
      <input
        type="text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="add…"
        onKeyDown={(e) => {
          if (e.key === 'Enter' && text.trim()) {
            onChange([...values, text.trim()]);
            setText('');
          }
        }}
      />
    </span>
  );
}
